import { Component, Element, Event, EventEmitter, JSX, Listen, Prop, h } from '@stencil/core'

import { KeyCode } from '../../enums/keycode.enum'
import { documentProvider } from '../../utils/documentProvider'
import { isMobile } from '../../utils/matchMedia'
import { windowProvider } from '../../utils/windowProvider'

type DropdownBasePlacement = 'top' | 'bottom'

@Component({
  tag: 'air-dropdown-base',
  styleUrl: 'air-dropdown-base.scss',
  shadow: true,
})
export class AirDropdownBase {
  /** Reference to the host element */
  @Element() host!: HTMLAirDropdownBaseElement

  /** Defines the open state */
  @Prop({ mutable: true, reflect: true }) opened = false

  /** Specifies if dropdown should be disabled */
  @Prop({ reflect: true }) disabled = false

  /** Defines trigger element */
  @Prop() trigger?: HTMLElement

  /** Triggers when dropdown is open */
  @Event({ eventName: 'air-open' }) open!: EventEmitter<void>

  /** Triggers when dropdown is closed */
  @Event({ eventName: 'air-close' }) close!: EventEmitter<void>

  /** Listen click event outside to close dropdown */
  @Listen('click', { target: 'window', capture: true })
  handleClickOutside(event: MouseEvent): void {
    if (!this.opened) {
      return
    }

    const composedPath = event.composedPath !== undefined ? event.composedPath() : []

    if (
      composedPath.includes(this.host) ||
      (this.triggerElement && composedPath.includes(this.triggerElement))
    ) {
      return
    }

    this.onClose()
  }

  private triggerElement?: HTMLElement
  private contentElement?: HTMLElement
  private contentPositionAnimationRequestId!: number

  componentDidRender(): void {
    this.contentPositionAnimationRequestId = this.contentPositionAnimationFrame()

    if (this.trigger) {
      this.triggerElement = this.trigger

      this.triggerElement.removeEventListener('click', this.handleTriggerClick)
      this.triggerElement.addEventListener('click', this.handleTriggerClick)
    }
  }

  disconnectedCallback(): void {
    windowProvider().cancelAnimationFrame(this.contentPositionAnimationRequestId)

    this.triggerElement?.removeEventListener('click', this.handleTriggerClick)
  }

  private getTriggerPosition(): { x: number; y: number } {
    const { x, y, height } = this.triggerElement?.getBoundingClientRect() || {}
    return { x: x || 0, y: (y || 0) + (height || 0) }
  }

  private getContentTopPosition(triggerY: number): number {
    return (
      triggerY - (this.triggerElement?.offsetHeight || 0) - (this.contentElement?.offsetHeight || 0)
    )
  }

  private updateContentStyles(): void {
    if (!this.opened || !this.triggerElement) return

    const { x: triggerX, y: triggerY } = this.getTriggerPosition()
    const placement = this.getPlacement(this.triggerElement)
    const contentY = placement === 'top' ? this.getContentTopPosition(triggerY) : triggerY

    this.contentElement?.classList.remove('top', 'bottom')
    this.contentElement?.classList.add(placement)

    if (this.contentElement) {
      this.contentElement.style.top = `${contentY}px`
      this.contentElement.style.left = `${triggerX}px`
      this.contentElement.style.width =
        this.getContentWidth() !== undefined ? `${this.getContentWidth()}px` : ''
    }

    this.contentPositionAnimationFrame()
  }

  /**
   * The content position needs to be updated accordingly to trigger position on scroll
   */
  private contentPositionAnimationFrame(): number {
    return windowProvider().requestAnimationFrame(() => this.updateContentStyles())
  }

  private onOpen(): void {
    const event = this.open.emit()

    if (event.defaultPrevented) {
      return
    }

    this.opened = true
  }

  private onClose(): void {
    const event = this.close.emit()

    if (event.defaultPrevented) {
      return
    }

    this.opened = false
  }

  private handleTriggerClick = (event: MouseEvent): void => {
    event.stopPropagation()
    if (this.disabled) return
    this.opened ? this.onClose() : this.onOpen()
  }

  private handleContentKeyDown = (event: KeyboardEvent): void => {
    if (KeyCode.Escape === event.code) {
      this.opened = false
    }
  }

  private getContentWidth(): number | undefined {
    if (!isMobile()) return
    const { width, paddingLeft, paddingRight } = windowProvider().getComputedStyle(
      documentProvider().body,
    )
    return parseFloat(width) - parseFloat(paddingLeft) - parseFloat(paddingRight)
  }

  private getPlacement(trigger: HTMLElement): DropdownBasePlacement {
    const { innerHeight: windowHeight } = windowProvider()
    const { height: triggerHeight, y: triggerY } = trigger.getBoundingClientRect()
    const triggerCenterTop = triggerY + triggerHeight / 2
    let placement: DropdownBasePlacement = 'bottom'

    if (triggerCenterTop >= windowHeight / 2) {
      placement = 'top'
    }

    return placement
  }

  render(): JSX.Element {
    const containerAttributes = {
      class: 'container',
    }

    const triggerAttributes = {
      class: 'trigger',
      ref: (element?: HTMLElement): void => {
        this.triggerElement = element
      },
      onClick: this.handleTriggerClick,
    }

    const hidden = !this.opened || this.disabled
    const contentAttributes = {
      class: 'content',
      [`aria-hidden`]: hidden,
      hidden: hidden,
      ref: (element?: HTMLElement): void => {
        this.contentElement = element
      },
      onKeyDown: this.handleContentKeyDown,
    }

    return (
      <div {...containerAttributes}>
        {!this.trigger && (
          <div {...triggerAttributes}>
            <slot name="trigger"></slot>
          </div>
        )}
        <div {...contentAttributes}>
          <slot></slot>
        </div>
      </div>
    )
  }
}
